import React, { useEffect, useRef, useState } from 'react';
import '../styles/CancionCurrentIssueDash.css';
import { IoMdRefresh } from 'react-icons/io';
import axios from 'axios';
import config from '../../../constants/config';
import { useOutletContext } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useToast } from '../../../hooks/ToastContext';

export default function CancionCurrentIssueDash() {
    const { account } = useOutletContext() || {};
    const [cookies] = useCookies(["account-canto"]);
    const [currentIssue, setCurrentIssue] = useState({
        id: null,
        title: '',
        summary: '',
        image: '',
        flipbook_url: ''
    });
    const [loading, setLoading] = useState(false);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [selectedImagePreview, setSelectedImagePreview] = useState('');
    const fileInputRef = useRef(null);
    const { showToast } = useToast();

    useEffect(() => {
        fetchCurrentIssue();
    }, []);

    const fetchCurrentIssue = () => {
        setLoading(true);
        
        axios.post(config.api.url + 'admin/current_issue/fetch', {}, {
            crossdomain: true,
            headers: {
                'api': config.api.key,
                'auth': account?.auth_token || cookies['account-canto']
            }
        })
        .then((response) => {
            const { success, data } = response.data;
            if (success && data) {
                setCurrentIssue(data);
                // Add cache-busting parameter to force image reload
                const imageUrl = data.image ? `${data.image}?t=${Date.now()}` : '';
                setSelectedImagePreview(imageUrl);
            }
            setLoading(false);
        })
        .catch((error) => {
            console.error('Error fetching current issue:', error);
            setLoading(false);
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentIssue((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setSelectedImageFile(file);
            setSelectedImagePreview(imageUrl);
        }
    };

    const handleSelectImage = () => {
        fileInputRef.current.click();
    };

    const handleSaveChanges = () => {
        if (loading) return;
        setLoading(true);

        const formData = new FormData();
        formData.append("id", currentIssue.id);
        formData.append("title", currentIssue.title);
        formData.append("summary", currentIssue.summary);
        formData.append("flipbook_url", currentIssue.flipbook_url || '');
        
        if (selectedImageFile) {
            formData.append("image", selectedImageFile);
        }
        
        axios.post(config.api.url + 'admin/current_issue/update', formData, {
            crossdomain: true,
            headers: {
                api: config.api.key,
                auth: account?.auth_token || cookies['account-canto'],
                "Content-Type": "multipart/form-data"
            }
        })
        .then((response) => {
            const { success, response: responseMsg } = response.data;
            if (success) {
                showToast({ 
                    message: 'Current issue updated successfully!', 
                    type: 'success' 
                });
                setSelectedImageFile(null);
                fetchCurrentIssue();
            } else {
                showToast({ 
                    message: 'Error updating current issue: ' + responseMsg, 
                    type: 'error' 
                });
            }
            setLoading(false);
        })
        .catch((error) => {
            console.error('Update error:', error);
            showToast({ 
                message: 'Error updating current issue. Please try again.', 
                type: 'error' 
            });
            setLoading(false);
        });
    };

    return (
        <div className='CancionCurrentIssueDash'>
            <div className='CancionCurrentIssueHeader'>
                <div className='CancionCurrentIssueHeaderTitle'>
                    <h1>CANCION Current Issue</h1>
                    <p>Manage the current issue displayed on the CANCION Magazine page</p>
                </div>
                <div className='CancionCurrentIssueHeaderActions'>
                    <button className='CancionCurrentIssueRefreshButton' onClick={fetchCurrentIssue}>
                        <IoMdRefresh />
                        <span>Refresh</span>
                    </button>
                    <button 
                        className='CancionCurrentIssueSaveButton'
                        onClick={handleSaveChanges}
                        disabled={loading}
                    >
                        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                </div>
            </div>

            <div className='CancionCurrentIssueContent'>
                <div className='CancionCurrentIssueForm'>
                    <div className='CancionCurrentIssueOption'>
                        <div className='CancionCurrentIssueOptionTitle'>Cover Image</div>
                        <div className='CancionCurrentIssueOptionContent'>
                            <div 
                                className='CancionCurrentIssueImagePreview' 
                                style={{ backgroundImage: `url(${selectedImagePreview})` }}
                                onClick={handleSelectImage}
                            ></div>
                            <input 
                                type='file' 
                                ref={fileInputRef} 
                                style={{ display: 'none' }} 
                                onChange={handleImageChange} 
                                accept='image/*'
                            />
                            <div className='CancionCurrentIssueImageText'>Click to change image</div>
                        </div>
                    </div>

                    <div className='CancionCurrentIssueOption'>
                        <div className='CancionCurrentIssueOptionTitle'>Title</div>
                        <div className='CancionCurrentIssueOptionContent'>
                            <input 
                                type='text' 
                                name='title' 
                                value={currentIssue.title} 
                                onChange={handleChange} 
                                className='CancionCurrentIssueInput'
                                placeholder='Enter issue title'
                            />
                        </div>
                    </div>

                    <div className='CancionCurrentIssueOption'>
                        <div className='CancionCurrentIssueOptionTitle'>Summary</div>
                        <div className='CancionCurrentIssueOptionContent'>
                            <textarea 
                                name='summary' 
                                value={currentIssue.summary} 
                                onChange={handleChange} 
                                className='CancionCurrentIssueTextarea'
                                rows='10'
                                placeholder='Enter issue summary'
                            />
                        </div>
                    </div>

                    <div className='CancionCurrentIssueOption'>
                        <div className='CancionCurrentIssueOptionTitle'>Flipbook URL (Optional)</div>
                        <div className='CancionCurrentIssueOptionContent'>
                            <input 
                                type='text' 
                                name='flipbook_url' 
                                value={currentIssue.flipbook_url || ''} 
                                onChange={handleChange} 
                                className='CancionCurrentIssueInput'
                                placeholder='Enter flipbook URL'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
